import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import { FormModal, Field, TextArea, SelectField } from "./LeadsPage";
import { Plus, Pencil, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import SendInvoiceModal from "../components/SendInvoiceModal";

const STATUSES = ["unpaid", "paid", "overdue"];
const STATUS_PILL = { unpaid: "pill-warning", paid: "pill-success", overdue: "pill-error" };

const blank = { contact_name: "", quote_id: "", amount: 0, status: "unpaid", due_date: "", notes: "" };

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [sending, setSending] = useState(null);

  const load = async () => setItems((await api.get("/invoices")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...blank, ...item }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) || 0 };
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Invoice deleted");
    load();
  };

  const total = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const paid = items.filter((i) => i.status === "paid").reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-label">Billing</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Invoices</h1>
        </div>
        <button data-testid="invoices-create-button" onClick={openCreate} className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-100 transition">
          <Plus className="w-4 h-4" /> New invoice
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <GlassPanel className="!p-5" delay={0.02}>
          <div className="text-label">Total invoiced</div>
          <div className="font-heading text-3xl text-white mt-2">${total.toLocaleString()}</div>
        </GlassPanel>
        <GlassPanel className="!p-5" delay={0.05}>
          <div className="text-label">Paid</div>
          <div className="font-heading text-3xl text-emerald-300 mt-2">${paid.toLocaleString()}</div>
        </GlassPanel>
        <GlassPanel className="!p-5 hidden md:block" delay={0.08}>
          <div className="text-label">Outstanding</div>
          <div className="font-heading text-3xl text-amber-300 mt-2">${(total - paid).toLocaleString()}</div>
        </GlassPanel>
      </div>

      <GlassPanel className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-white/5 text-left">
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Amount</th>
                <th className="px-5 py-4">Due</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (<tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500 italic">No invoices yet.</td></tr>)}
              {items.map((it) => (
                <tr key={it.id} data-testid={`invoice-row-${it.id}`} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-white">{it.contact_name}</td>
                  <td className="px-5 py-3 text-white font-medium">${Number(it.amount).toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-400">{it.due_date || "—"}</td>
                  <td className="px-5 py-3"><span className={`pill ${STATUS_PILL[it.status] || "pill-muted"}`}>{it.status}</span></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1.5 flex-wrap justify-end">
                      <button data-testid={`invoice-send-${it.id}`} onClick={() => setSending(it)} title="Send / collect payment" className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 flex items-center gap-1 text-xs px-2"><Send className="w-3 h-3" /> Pay link</button>
                      <button data-testid={`invoice-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`invoice-delete-${it.id}`} onClick={() => remove(it.id)} className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassPanel>

      {open && (
        <FormModal title={editing ? "Edit invoice" : "New invoice"} onClose={() => setOpen(false)} onSubmit={save}>
          <Field label="Contact name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testId="invoice-contact" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount ($)" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="invoice-amount" required />
            <Field label="Due date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="invoice-due" />
          </div>
          <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} testId="invoice-status" />
          <TextArea label="Internal Shop Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="invoice-notes" />
        </FormModal>
      )}

      {sending && <SendInvoiceModal invoice={sending} onClose={() => setSending(null)} onRefresh={load} />}
    </div>
  );
}
